import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { setAuthToken } from '@/lib/api';
import { postAuthDestination } from '@/lib/home';

function describeError(e) {
  // 1) Backend returned a structured error
  const detail = e?.response?.data?.detail;
  if (detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation: extract human messages
      return detail.map((d) => d.msg || d.message || JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') return JSON.stringify(detail);
  }
  // 2) Backend responded but with no detail
  if (e?.response) {
    return `Server returned ${e.response.status} ${e.response.statusText || ''}`.trim();
  }
  // 3) No response at all (network / CORS / DNS / preflight)
  if (e?.message?.toLowerCase?.().includes('network')) {
    return 'Network error — please check your connection and try again.';
  }
  if (e?.code === 'ECONNABORTED') {
    return 'Request timed out — please try again.';
  }
  return e?.message || 'Authentication failed (unknown error).';
}

export default function AuthPage() {
  const { user, login, register, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // dest is the page the user was trying to reach before being bounced
  // through /auth. `postAuthDestination` filters out junk (root, /auth) and
  // falls back to the user's `default_tab` preference when the dest isn't a
  // real interior page.
  const dest = (loc.state && loc.state.from) || '/';
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errorBanner, setErrorBanner] = useState('');

  // Defensive: if the stored token is bad, clear it on mount of /auth
  useEffect(() => {
    if (!user && !loading) {
      const t = localStorage.getItem('tagedin_token');
      if (t) {
        setAuthToken(null);
      }
    }
  }, [user, loading]);

  // If already authenticated, redirect via Navigate (NOT during-render side-effect).
  if (!loading && user) {
    return <Navigate to={postAuthDestination(user, dest)} replace />;
  }

  const submit = async () => {
    setErrorBanner('');
    if (!email || !password) {
      setErrorBanner('Email and password are required.');
      return;
    }
    if (mode === 'register' && !username) {
      setErrorBanner('Username is required.');
      return;
    }
    setBusy(true);
    try {
      let freshUser;
      if (mode === 'login') {
        freshUser = await login(email.trim(), password);
      } else {
        freshUser = await register({
          email: email.trim(),
          username: username.trim(),
          password,
          display_name: (displayName || username).trim(),
        });
      }
      // Phase 23 — email verification REMOVED. Both login and register flows
      // now route directly to the user's preferred home (default_tab) with
      // no verification detour.
      nav(postAuthDestination(freshUser, dest), { replace: true });
    } catch (e) {
      const msg = describeError(e);
      setErrorBanner(msg);
      toast.error(msg);
      // Log full error to console to help debugging from devtools
      console.error('[auth] failure:', e?.response?.status, e?.response?.data, e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[hsl(var(--app-bg))] text-foreground">
      <div className="haze noise-overlay min-h-[100dvh]">
        <div className="mx-auto grid min-h-[100dvh] max-w-[1120px] grid-cols-1 gap-10 px-6 py-10 md:grid-cols-2 md:items-center">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">tagedin · chronology-first</div>
            <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Real time. Hashtag driven.<br />
              <span style={{ color: 'hsl(var(--feed-accent))' }}>No algorithm gatekeeper.</span>
            </h1>
            <p className="mt-4 max-w-prose text-muted-foreground">
              Post once, reach every relevant hashtag feed in chronological order. Capture in one tap. Tag people privately. Run blasts, communities, and funds — with transparent fees.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 font-mono text-xs text-muted-foreground">
              <li>• 6 specialized feeds</li>
              <li>• Unlimited hashtags</li>
              <li>• Private account tags</li>
              <li>• Geo marketplace</li>
              <li>• Community funds</li>
              <li>• IP geo-block</li>
            </ul>
          </div>
          <div className="rounded-[var(--radius)] border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-6 shadow-[var(--shadow-elev-2)]">
            {errorBanner && (
              <div className="mb-3 rounded-lg border border-[hsl(var(--destructive)/0.5)] bg-[hsl(var(--destructive)/0.12)] px-3 py-2 text-sm text-[hsl(var(--destructive-foreground))]" data-testid="auth-error-banner">
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--destructive))]">Error</span>
                <div className="mt-1">{errorBanner}</div>
              </div>
            )}
            <Tabs value={mode} onValueChange={(v) => { setMode(v); setErrorBanner(''); }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="auth-tab-login">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-tab-register">Create account</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="space-y-3 pt-4">
                <div><Label>Email or username</Label><Input data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="text" autoComplete="username" placeholder="you@example.com or yourhandle" /></div>
                <div><Label>Password</Label><Input data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" /></div>
                <Button className="w-full" onClick={submit} disabled={busy} data-testid="auth-submit-button">{busy ? 'Working…' : 'Login'}</Button>
                <div className="pt-1 text-right">
                  <Link
                    to="/forgot-password"
                    className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
                    data-testid="auth-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </TabsContent>
              <TabsContent value="register" className="space-y-3 pt-4">
                <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="auth-email-input" /></div>
                <div><Label>Username</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="yourhandle" data-testid="auth-username-input" /></div>
                <div><Label>Display name</Label><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" data-testid="auth-displayname-input" /></div>
                <div><Label>Password</Label><Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" data-testid="auth-password-input" /></div>
                <Button className="w-full" onClick={submit} disabled={busy} data-testid="auth-submit-button">{busy ? 'Working…' : 'Create account'}</Button>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
